// MARK://dhk8/admin-shell/lib/queries/catalog
// Queries tipadas (typed-document-node) del catálogo tenant-scoped — F1.1 / ADR-0009.
// El cliente Admin lee productos/categorías/insumos por el grafo federado
// (subgraph base erp-products), NO por REST `/v1/products`. El tenant lo impone
// el JWT en cada subgraph (RLS dhk8_app); NUNCA se manda `tenantId` como argumento
// (anti-IDOR). El shape de página es el canónico `ProductPage { items,total,page,limit }`.
import { graphql } from '../../gql/index.js'

// Listado de productos del tenant. `type` filtra PRODUCT/INGREDIENT/COMBO/SERVICE;
// sin `type` trae todos. La paginación la maneja el caller (tope backend = 100).
export const AdminProductsQuery = graphql(`
  query AdminProducts($type: String, $page: Int, $limit: Int) {
    products(type: $type, page: $page, limit: $limit) {
      total
      page
      limit
      items {
        id
        tenantId
        sku
        name
        description
        type
        priceCents
        currency
        active
        categoryId
        imageUrl
        unit
        tags
        tracksInventory
        consumesRecipe
        satProductKey
        satUnitKey
      }
    }
  }
`)

// Producto singular por id (erp-products, tenant del JWT, RLS). null si no existe
// o es de otro tenant (IDOR-guard del subgraph).
export const AdminProductQuery = graphql(`
  query AdminProduct($id: ID!) {
    product(id: $id) {
      id
      tenantId
      sku
      name
      description
      type
      priceCents
      currency
      active
      categoryId
      imageUrl
      unit
      tags
      tracksInventory
      consumesRecipe
      satProductKey
      satUnitKey
    }
  }
`)

// Combos-como-productos (v1): productos type=PRODUCT cuya categoría se llama
// "Combos". Selecciona category { name } para filtrar client-side (no hay
// entidad Combo propia todavía). tenant del JWT (RLS).
export const AdminComboProductsQuery = graphql(`
  query AdminComboProducts($page: Int, $limit: Int) {
    products(type: "PRODUCT", page: $page, limit: $limit) {
      total
      items {
        id
        name
        priceCents
        category {
          name
        }
      }
    }
  }
`)

export const AdminCategoriesQuery = graphql(`
  query AdminCategories {
    categories {
      id
      tenantId
      name
      parentId
      stationId
      sortOrder
      active
      publicVisible
    }
  }
`)

// NB: las conversiones de unidad (unitConversions / upsert / delete) viven ahora en
// queries/units.queries.ts (nueva forma sin input, con `global`). Ref: catálogo de
// unidades, sección "Equivalencias".
export const AdminTaxProfilesQuery = graphql(`
  query AdminTaxProfiles {
    taxProfiles {
      id
      tenantId
      code
      name
      impuesto
      tipoFactor
      tasaOCuota
      objetoImp
      traslado
      retencion
      validFrom
      validTo
      active
    }
  }
`)
// Listas de precio (F1.5): catalog-pricing. items = overrides productId→priceCents.
export const AdminPriceListsQuery = graphql(`
  query AdminPriceLists($limit: Int) {
    priceLists(limit: $limit) {
      items {
        id
        name
        active
        items {
          productId
          priceCents
        }
      }
    }
  }
`)

// Variantes (F1.4): viven inline en el Product (erp-products). El tab de Variantes
// del Admin las sintetiza haciendo flat-map de los productos tipo PRODUCT.
export const AdminProductVariantsQuery = graphql(`
  query AdminProductVariants($page: Int, $limit: Int) {
    products(type: "PRODUCT", page: $page, limit: $limit) {
      total
      items {
        id
        variants {
          id
          name
          priceCents
        }
      }
    }
  }
`)


// ── F8.x: escrituras del catálogo por el grafo (erp-products, tenant del JWT, RLS) ──
export const AdminCreateProductMutation = graphql(`
  mutation AdminCreateProduct($input: ErpProductCreateInput!) {
    createProduct(input: $input) {
      id
      tenantId
      sku
      name
      description
      type
      priceCents
      currency
      active
      categoryId
      imageUrl
      unit
      tags
      tracksInventory
      consumesRecipe
      satProductKey
      satUnitKey
    }
  }
`)

export const AdminUpdateProductMutation = graphql(`
  mutation AdminUpdateProduct($id: ID!, $input: ErpProductUpdateInput!) {
    updateProduct(id: $id, input: $input) {
      id
      tenantId
      sku
      name
      description
      type
      priceCents
      currency
      active
      categoryId
      imageUrl
      unit
      tags
      tracksInventory
      consumesRecipe
      satProductKey
      satUnitKey
    }
  }
`)

export const AdminSetProductAvailabilityMutation = graphql(`
  mutation AdminSetProductAvailability($id: ID!, $available: Boolean!) {
    setProductAvailability(id: $id, available: $available) {
      id
      active
    }
  }
`)

export const AdminDeleteProductMutation = graphql(`
  mutation AdminDeleteProduct($id: ID!) {
    deleteProduct(id: $id)
  }
`)

export const AdminCreateProductCategoryMutation = graphql(`
  mutation AdminCreateProductCategory($input: ErpProductCategoryCreateInput!) {
    createProductCategory(input: $input) {
      id
      tenantId
      name
      parentId
      stationId
      sortOrder
      active
      publicVisible
    }
  }
`)

export const AdminUpdateProductCategoryMutation = graphql(`
  mutation AdminUpdateProductCategory($id: ID!, $input: ErpProductCategoryUpdateInput!) {
    updateProductCategory(id: $id, input: $input) {
      id
      tenantId
      name
      parentId
      stationId
      sortOrder
      active
      publicVisible
    }
  }
`)

export const AdminDeleteProductCategoryMutation = graphql(`
  mutation AdminDeleteProductCategory($id: ID!) {
    deleteProductCategory(id: $id)
  }
`)

// ── F9.4: escrituras de variantes por el grafo (erp-products, product-scoped, IDOR-guard) ──
export const AdminAddProductVariantMutation = graphql(`
  mutation AdminAddProductVariant($productId: ID!, $input: ErpProductVariantCreateInput!) {
    addProductVariant(productId: $productId, input: $input) {
      id
      productId
      sku
      name
      priceCents
      active
    }
  }
`)

export const AdminChangeProductVariantPriceMutation = graphql(`
  mutation AdminChangeProductVariantPrice($productId: ID!, $variantId: ID!, $priceCents: Int!) {
    changeProductVariantPrice(productId: $productId, variantId: $variantId, priceCents: $priceCents) {
      id
      productId
      sku
      name
      priceCents
      active
    }
  }
`)

export const AdminDeleteProductVariantMutation = graphql(`
  mutation AdminDeleteProductVariant($productId: ID!, $variantId: ID!) {
    deleteProductVariant(productId: $productId, variantId: $variantId)
  }
`)

// ── F9.4: escrituras de listas de precio por el grafo (catalog-pricing, RLS, IDOR-guard) ──
export const AdminCreatePriceListMutation = graphql(`
  mutation AdminCreatePriceList($input: PriceListCreateInput!) {
    createPriceList(input: $input) {
      id
      name
      active
      items {
        productId
        priceCents
      }
    }
  }
`)

export const AdminUpdatePriceListMutation = graphql(`
  mutation AdminUpdatePriceList($id: ID!, $input: PriceListUpdateInput!) {
    updatePriceList(id: $id, input: $input) {
      id
      name
      active
      items {
        productId
        priceCents
      }
    }
  }
`)

export const AdminDeletePriceListMutation = graphql(`
  mutation AdminDeletePriceList($id: ID!) {
    deletePriceList(id: $id)
  }
`)

// Override de precio por producto dentro de una lista (upsert) y su retiro.
// El item se identifica por (listId, productId) — addPriceListItem hace upsert.
export const AdminAddPriceListItemMutation = graphql(`
  mutation AdminAddPriceListItem($listId: ID!, $input: PriceListItemAddInput!) {
    addPriceListItem(listId: $listId, input: $input) {
      productId
      priceCents
    }
  }
`)

export const AdminRemovePriceListItemMutation = graphql(`
  mutation AdminRemovePriceListItem($listId: ID!, $itemId: ID!) {
    removePriceListItem(listId: $listId, itemId: $itemId)
  }
`)

// ── A5: variantes multi-eje por el grafo (catalog-variants, tenant del JWT, RLS) ──
// VariantAxis (eje, ej "Tamaño") → VariantOption por eje (label + delta) →
// VariantSku (combinación de opciones con precio). Lectura + escritura por producto.
export const AdminVariantAxesQuery = graphql(`
  query AdminVariantAxes($productId: ID!) {
    variantAxes(productId: $productId) {
      id
      productId
      name
      sortOrder
      options {
        id
        axisId
        label
        priceDeltaCents
        sortOrder
      }
    }
  }
`)

export const AdminVariantSkusQuery = graphql(`
  query AdminVariantSkus($productId: ID!) {
    variantSkus(productId: $productId) {
      id
      productId
      sku
      optionIds
      priceCents
      priceMode
      active
    }
  }
`)

export const AdminUpsertVariantAxisMutation = graphql(`
  mutation AdminUpsertVariantAxis($input: VariantAxisInput!) {
    upsertVariantAxis(input: $input) {
      id
      productId
      name
      sortOrder
      options {
        id
        axisId
        label
        priceDeltaCents
        sortOrder
      }
    }
  }
`)

export const AdminDeleteVariantAxisMutation = graphql(`
  mutation AdminDeleteVariantAxis($id: ID!) {
    deleteVariantAxis(id: $id)
  }
`)

export const AdminUpsertVariantOptionMutation = graphql(`
  mutation AdminUpsertVariantOption($input: VariantOptionInput!) {
    upsertVariantOption(input: $input) {
      id
      axisId
      label
      priceDeltaCents
      sortOrder
    }
  }
`)

export const AdminDeleteVariantOptionMutation = graphql(`
  mutation AdminDeleteVariantOption($id: ID!) {
    deleteVariantOption(id: $id)
  }
`)

export const AdminUpsertVariantSkuMutation = graphql(`
  mutation AdminUpsertVariantSku($input: VariantSkuInput!) {
    upsertVariantSku(input: $input) {
      id
      productId
      sku
      optionIds
      priceCents
      priceMode
      active
    }
  }
`)

export const AdminDeleteVariantSkuMutation = graphql(`
  mutation AdminDeleteVariantSku($id: ID!) {
    deleteVariantSku(id: $id)
  }
`)
